import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import GlobalFormShell from "../../../../../global/forms/GlobalFormShell";
import {
  GlobalButton,
  GlobalSelectField,
  GlobalTextField,
} from "../../../../../global/forms/GlobalFormFields";
import { VISITOR_MASTER_STATUS_OPTIONS } from "../../../utils/visitorMasterStatusOptions";
import {
  visitorIdProofSchema,
  visitorIdProofFormDefaults,
} from "../../../utils/visitorIdProofValidation";
import { mapApiDetailsToFormErrors } from "../../../utils/visitorHelpers";

const ALERT_ID = "visitor-id-proof-form-alert";

export default function IdProofForm({
  mode = "create",
  initialValues,
  onSubmit,
  onCancel,
  submitting = false,
  serverError = "",
}) {
  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(visitorIdProofSchema),
    defaultValues: initialValues || visitorIdProofFormDefaults,
    mode: "onBlur",
  });

  useEffect(() => {
    if (initialValues) reset(initialValues);
  }, [initialValues, reset]);

  const applyServerDetails = (details) => {
    const mapped = mapApiDetailsToFormErrors(details);
    Object.entries(mapped).forEach(([field, message]) => {
      setError(field, { type: "server", message });
    });
  };

  const submitHandler = async (values) => {
    try {
      await onSubmit?.({
        type_name: String(values.type_name ?? "").trim(),
        status_id: Number(values.status_id),
      });
    } catch (err) {
      if (err?.details) applyServerDetails(err.details);
      throw err;
    }
  };

  return (
    <form onSubmit={handleSubmit(submitHandler)} noValidate>
      <GlobalFormShell
        legend={mode === "edit" ? "Edit ID proof type" : "Add ID proof type"}
        alertId={ALERT_ID}
        showAlert={Boolean(serverError)}
        alertText={serverError}
        gridClassName="gfs-form-grid--compact"
        actions={
          <>
            {onCancel ? (
              <GlobalButton type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
                Cancel
              </GlobalButton>
            ) : null}
            <GlobalButton type="submit" variant="primary" disabled={submitting}>
              {submitting ? "Saving…" : mode === "edit" ? "Save changes" : "Create type"}
            </GlobalButton>
          </>
        }
      >
        <Controller
          name="type_name"
          control={control}
          render={({ field }) => (
            <GlobalTextField
              id="type_name"
              label="Type name"
              required
              value={field.value}
              onChange={field.onChange}
              error={errors.type_name?.message}
              placeholder="e.g. Aadhaar, Driving licence"
              baseAlertId={ALERT_ID}
            />
          )}
        />
        <Controller
          name="status_id"
          control={control}
          render={({ field }) => (
            <GlobalSelectField
              id="id_proof_status_id"
              label="Status"
              required
              value={field.value}
              onChange={field.onChange}
              error={errors.status_id?.message}
              options={VISITOR_MASTER_STATUS_OPTIONS}
              placeholderOption="Select status"
              baseAlertId={ALERT_ID}
            />
          )}
        />
      </GlobalFormShell>
    </form>
  );
}
